import { useState, useEffect, useCallback, useRef } from "react";
import { eventListenerService } from "../services/eventListener";
import { DisplayEvent, EventQueueState } from "../types/events";

interface UseEventBannerConfig {
  enabled: boolean;
  eventDisplayDuration?: number;
  oldEventCycleDelay?: number; // Delay before cycling old events (ms)
  oldEventCycleDelayFluctuation?: number; // +-Fluctuation range for old event cycle delay (ms)
  maxOldEvents?: number; // Max number of old events to keep
}

interface UseEventBannerResult {
  currentEvent: DisplayEvent | null;
  isLoading: boolean;
  error: string | null;
  closeCurrentEvent: () => void;
  recentEventsCount: number;
}

export function useEventBanner(config: UseEventBannerConfig): UseEventBannerResult {
  const {
    enabled,
    eventDisplayDuration = 2000,
    oldEventCycleDelay = 8000,
    oldEventCycleDelayFluctuation = 10000,
    maxOldEvents = 100,
  } = config;

  const [queueState, setQueueState] = useState<EventQueueState>({
    events: [],
    currentEvent: null,
    isDisplaying: false,
    nextDisplayTime: 0,
    queueIndex: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cycleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hookIdRef = useRef<string>(`event-banner-${Math.random().toString(36).substr(2, 9)}`);

  // Clear timeouts
  const clearTimeouts = useCallback(() => {
    if (displayTimeoutRef.current) {
      clearTimeout(displayTimeoutRef.current);
      displayTimeoutRef.current = null;
    }
    if (cycleTimeoutRef.current) {
      clearTimeout(cycleTimeoutRef.current);
      cycleTimeoutRef.current = null;
    }
  }, []);

  // Display an event
  const displayEvent = useCallback(
    (event: DisplayEvent) => {
      setQueueState((prev) => ({
        ...prev,
        currentEvent: event,
        isDisplaying: true,
        nextDisplayTime:
          Date.now() +
          oldEventCycleDelay +
          Math.random() * oldEventCycleDelayFluctuation -
          oldEventCycleDelayFluctuation / 2,
      }));
      displayTimeoutRef.current = setTimeout(() => {
        setQueueState((prev) => ({
          ...prev,
          currentEvent: null,
          isDisplaying: false,
        }));
        displayTimeoutRef.current = null;
      }, eventDisplayDuration);
    },
    [eventDisplayDuration, oldEventCycleDelay, oldEventCycleDelayFluctuation]
  );

  // Schedule cycling through old events
  const scheduleOldEventCycle = useCallback(() => {
    if (!enabled || queueState.isDisplaying) return;

    // const oldEvents = queueState.events.filter((event) => !event.isNew);
    const oldEvents = queueState.events; // no need to filter since the events in queueState are all old events
    if (oldEvents.length === 0) return;

    // Check if enough time has passed since last display event
    const now = Date.now();
    if (now < queueState.nextDisplayTime) {
      const remainingDelay = queueState.nextDisplayTime - now;
      cycleTimeoutRef.current = setTimeout(scheduleOldEventCycle, remainingDelay);
      return;
    }

    // Display next old event
    const eventToShow = oldEvents[queueState.queueIndex % oldEvents.length];
    displayEvent(eventToShow);

    setQueueState((prev) => ({
      ...prev,
      queueIndex: (prev.queueIndex + 1) % oldEvents.length,
    }));

    // Schedule next cycle
    cycleTimeoutRef.current = setTimeout(() => {
      scheduleOldEventCycle();
    }, eventDisplayDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queueState, oldEventCycleDelay, eventDisplayDuration, displayEvent]);

  // Handle new events from the service
  const handleNewEvent = useCallback(
    (event: DisplayEvent) => {
      if (!enabled) return;

      setQueueState((prev) => {
        const existingIndex = prev.events.findIndex((e) => e.id === event.id);

        if (existingIndex < 0) {
          return prev;
        }
        // Add new event to the latest
        const newEvents = [...prev.events.slice(0, maxOldEvents - 1), { ...event, isNew: false }];

        return {
          ...prev,
          events: newEvents,
        };
      });
      // Display new events immediately
      if (event.isNew) {
        clearTimeouts();
        displayEvent(event);
      }
    },
    [enabled, maxOldEvents, displayEvent, clearTimeouts]
  );

  // Close current event manually
  const closeCurrentEvent = useCallback(() => {
    clearTimeouts();
    setQueueState((prev) => ({
      ...prev,
      currentEvent: null,
      isDisplaying: false,
    }));
  }, [clearTimeouts]);

  // Track if events have been loaded to prevent multiple loads
  const eventsLoadedRef = useRef(false);

  // Load initial recent events
  const loadRecentEvents = useCallback(async () => {
    if (eventsLoadedRef.current || !enabled) return;

    setIsLoading(true);
    setError(null);
    eventsLoadedRef.current = true;

    try {
      console.log("Loading recent events...");
      const recentEvents = await eventListenerService.getRecentEvents(maxOldEvents);
      console.log("Loaded recent events:", recentEvents.length);

      setQueueState((prev) => ({
        ...prev,
        events: recentEvents,
        queueIndex: Math.max(0, recentEvents.length - 8),
      }));
    } catch (err) {
      console.error("Failed to load recent events", err);
      setError(err instanceof Error ? err.message : "Failed to load recent events");
      eventsLoadedRef.current = false; // Allow retry on error
    } finally {
      setIsLoading(false);
    }
  }, [enabled, maxOldEvents]);

  // Start/stop event cycling
  useEffect(() => {
    if (!enabled || queueState.isDisplaying) {
      clearTimeouts();
      return;
    }

    // Start cycling after delay
    cycleTimeoutRef.current = setTimeout(() => {
      scheduleOldEventCycle();
    }, 2000); // Initial delay before starting cycles

    return clearTimeouts;
  }, [enabled, queueState.isDisplaying, scheduleOldEventCycle, clearTimeouts]);

  // Setup event listener service (only once when enabled becomes true)
  useEffect(() => {
    if (!enabled) {
      eventsLoadedRef.current = false; // Reset when disabled
      return;
    }

    const hookId = hookIdRef.current;

    // Subscribe to new events
    eventListenerService.subscribe(hookId, handleNewEvent);

    // Load recent events only once
    loadRecentEvents();

    return () => {
      eventListenerService.unsubscribe(hookId);
      clearTimeouts();
    };
  }, [enabled, handleNewEvent, loadRecentEvents, clearTimeouts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const hookId = hookIdRef.current;
      eventListenerService.unsubscribe(hookId);
      clearTimeouts();
    };
  }, [clearTimeouts]);

  return {
    currentEvent: queueState.currentEvent,
    isLoading,
    error,
    closeCurrentEvent,
    recentEventsCount: queueState.events.length,
  };
}
